import { DataProvider } from 'react-admin';
import {
    DeleteBranchParams,
    DeleteBranchResult,
    TreeDataProvider,
    TreeRecord,
} from '../../types';

/**
 * If the API does not handle the children of a node when it is deleted, this function
 * augment the existing dataProvider with a deleteBranch implementation which will
 * delete all children first recursivly.
 *
 * @warning Do not use in production.
 *
 * The added Tree methods call the API way too much, sometimes asking for the
 * entire tree. In practice, you should implement an optimized API route for
 * each of the Tree methods.
 *
 * @param dataProvider The dataProvider with tree support
 */
export const deleteBranchByDeletingAllChildren = async <
    ResourceType extends string = string,
    RecordType extends TreeRecord = TreeRecord
>(
    dataProvider: DataProvider<ResourceType> & TreeDataProvider<ResourceType>,
    resource: ResourceType,
    params: DeleteBranchParams
): Promise<DeleteBranchResult> => {
    const deleteBranch = async params => {
        // Get the node children
        const { data: children } = await dataProvider.getChildNodes<RecordType>(
            resource,
            {
                parentId: params.id,
                meta: params.meta,
            }
        );

        if (children.length > 0) {
            // Recursively delete all the node children
            await Promise.all(
                children.map(child =>
                    deleteBranch({
                        id: child.id,
                        data: child,
                        meta: params.meta,
                    })
                )
            );
        }

        // Delete the node
        await dataProvider.delete<RecordType>(resource, params);
    };

    await deleteBranch(params);

    return { data: params.previousData };
};
